import { useEffect, useMemo, useState } from 'react'
import { Clock, Mail, Shield, UserCircle } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { useAuth } from '../hooks/useAuth'
import { getCurrentAdminUser, updateCurrentAdminUserPreferences, type DashboardUser } from '../lib/api/adminUsers'
import { getTourOperator } from '../lib/api/tourOperators'
import type { MeasurementUnit } from '../lib/measurements'

export const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth()
  const [account, setAccount] = useState<DashboardUser | null>(null)
  const [operatorName, setOperatorName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingMeasurementUnit, setSavingMeasurementUnit] = useState(false)
  const [measurementFeedback, setMeasurementFeedback] = useState<string | null>(null)

  useEffect(() => {
    void getCurrentAdminUser()
      .then((data) => setAccount(data))
      .catch((err) => console.error('Failed to load profile', err))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!account?.operatorId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: clear operator name when no operatorId
      setOperatorName(null)
      return
    }

    void getTourOperator(account.operatorId)
      .then((operator) => setOperatorName(operator.name))
      .catch((err) => {
        console.error('Failed to load operator', err)
        setOperatorName(null)
      })
  }, [account?.operatorId])

  const displayName = account?.displayName || user?.displayName || user?.email || 'Account Owner'
  const roleLabel = account?.role ?? user?.role ?? 'Guest'
  const measurementUnit = account?.measurementUnit ?? user?.measurementUnit ?? 'Metric'

  const accountMetadata = useMemo(
    () => [
      { label: 'Email', value: account?.email ?? user?.email ?? '—', icon: Mail },
      { label: 'Role', value: roleLabel, icon: Shield },
      { label: 'Operator Access', value: operatorName ?? (account?.operatorId ? 'Operator access' : 'None linked'), icon: UserCircle }
    ],
    [account?.email, account?.operatorId, operatorName, roleLabel, user?.email]
  )

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Account</p>
          <h1 className="text-3xl font-semibold text-foreground">{displayName}</h1>
          <p className="text-sm text-muted-foreground">Manage your Base Camp access and linked operator permissions.</p>
        </div>
        <Button variant="outline" onClick={logout}>
          Sign out
        </Button>
      </header>

      {roleLabel === 'Guest' && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-start gap-4 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-900">Access Pending</h3>
              <p className="text-sm text-orange-800">
                Your account is registered but you don&apos;t have access to dashboard features yet.
                Please contact an administrator to request access.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Account overview</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          {accountMetadata.map((item) => (
            <div key={item.label} className="rounded-2xl border border-border bg-card/50 p-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <item.icon className="h-4 w-4" />
                {item.label}
              </div>
              <p className="text-lg font-semibold text-foreground">{loading ? '—' : item.value}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Measurement preference</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label htmlFor="measurement-unit" className="block text-sm font-medium text-foreground">
              Dashboard units
            </label>
            <select
              id="measurement-unit"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={measurementUnit}
              disabled={loading || savingMeasurementUnit}
              onChange={(event) => {
                const nextUnit = event.target.value as MeasurementUnit
                setSavingMeasurementUnit(true)
                setMeasurementFeedback(null)
                void updateCurrentAdminUserPreferences({ measurementUnit: nextUnit })
                  .then((updated) => {
                    setAccount(updated)
                    updateUser(updated)
                    setMeasurementFeedback('Measurement preference saved.')
                  })
                  .catch((err) => {
                    console.error('Failed to save measurement preference', err)
                    setMeasurementFeedback('Unable to save measurement preference.')
                  })
                  .finally(() => setSavingMeasurementUnit(false))
              }}
            >
              <option value="Metric">Metric (km, m, kg)</option>
              <option value="Imperial">Imperial (mi, ft, lb)</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Dashboard forms and tables use this preference, while saved API values remain metric.
            </p>
            {measurementFeedback && (
              <p className={`text-xs ${measurementFeedback.startsWith('Unable') ? 'text-destructive' : 'text-emerald-600'}`}>
                {measurementFeedback}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Security & access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <DetailRow label="Last login" value={account?.lastLoginAt ? formatDateTime(account.lastLoginAt) : 'No logins recorded'} />
            <DetailRow label="Account created" value={account?.createdAt ? formatDateTime(account.createdAt) : '—'} />
            <DetailRow label="Last updated" value={account?.updatedAt ? formatDateTime(account.updatedAt) : '—'} />
          </CardContent>
        </Card>

        {account?.role === 'TourAdmin' && (
          <Card>
            <CardHeader>
              <CardTitle>Linked operator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <DetailRow label="Operator" value={operatorName ?? 'Loading…'} />
              <DetailRow
                label="Operator ID"
                value={account.operatorId ?? '—'}
              />
              <p className="text-xs text-muted-foreground">
                Tour administrators can only edit tours that belong to their assigned operator.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground">{value}</p>
  </div>
)

const formatDateTime = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}
